import type { FC } from 'react'
import { Box, Button, CircularProgress, Stack, Step, StepLabel, Stepper, Typography, useTheme } from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import { PackageCategoryCard } from 'pages/Admin/Rewards/components/PackageCategoryCard'
import { ReviewCategoryCard } from 'pages/Admin/Rewards/components/ReviewCategoryCard'

interface PackageRewardItem {
  rewardId: string
  rewardName: string
  categoryName: string
  categoryDisplayOrder: number
  rewardDisplayOrder: number
  selected: boolean
  companyContribution: number
  employeeContribution: number
  supportsEmployeeContribution: boolean
}

interface PackageCategoryGroup {
  categoryName: string
  categoryDisplayOrder: number
  rewards: PackageRewardItem[]
}

interface PackageTotals {
  company: number
  employee: number
  total: number
}

interface SmartImportPackageFormProps {
  activeStep: number
  name: string
  description: string
  disclaimer: string
  onNameChange: (value: string) => void
  onDescriptionChange: (value: string) => void
  onDisclaimerChange: (value: string) => void
  rewardsByCategory: PackageCategoryGroup[]
  selectedRewardsByCategory: PackageCategoryGroup[]
  selectedRewardsCount: number
  totals: PackageTotals
  allSelected: boolean
  someSelected: boolean
  missingRewardCount: number
  isSubmitting: boolean
  isNameValid: boolean
  canSubmit: boolean
  errorMessage?: string | null
  onToggleRewardSelect: (id: string) => void
  onContributionChange: (id: string, field: 'companyContribution' | 'employeeContribution', value: string) => void
  onReorderRewards: (categoryName: string, oldIndex: number, newIndex: number) => void
  onToggleAll: (checked: boolean) => void
  onNext: () => void
  onBack: () => void
  onCancel: () => void
  onCreate: () => void
}

const steps = ['Package Name', 'Select Rewards', 'Review']

const SmartImportPackageForm: FC<SmartImportPackageFormProps> = ({
  activeStep,
  name,
  description,
  disclaimer,
  onNameChange,
  onDescriptionChange,
  onDisclaimerChange,
  rewardsByCategory,
  selectedRewardsByCategory,
  selectedRewardsCount,
  totals,
  allSelected,
  someSelected,
  missingRewardCount,
  isSubmitting,
  isNameValid,
  canSubmit,
  errorMessage,
  onToggleRewardSelect,
  onContributionChange,
  onReorderRewards,
  onToggleAll,
  onNext,
  onBack,
  onCancel,
  onCreate,
}) => {
  const theme = useTheme()

  const formatCurrency = (value: number): string => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  }

  const renderStep1 = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
      <Paper sx={{ p: 4, maxWidth: 600, width: '100%' }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
          Package Details
        </Typography>

        <TextField
          autoFocus
          margin="dense"
          name="name"
          label="Package Name"
          variant="outlined"
          pill
          shrink
          fullWidth
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          required
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          name="description"
          label="Header Information"
          variant="outlined"
          pill
          shrink
          fullWidth
          multiline
          rows={4}
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
          placeholder="Brief description of this rewards package (max 4 lines)"
          sx={{ mb: 2 }}
        />

        <TextField
          margin="dense"
          name="disclaimer"
          label="Disclaimer"
          variant="outlined"
          pill
          shrink
          fullWidth
          multiline
          rows={3}
          value={disclaimer}
          onChange={(event) => onDisclaimerChange(event.target.value)}
          placeholder="Optional disclaimer text to display at the bottom of the rewards dashboard"
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
          Give your package a descriptive name. The header information will appear below the employee header on the
          rewards dashboard, and the disclaimer will appear at the bottom.
        </Typography>
      </Paper>
    </Box>
  )

  const renderStep2 = () => (
    <>
      {missingRewardCount > 0 && (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography color="textSecondary" textAlign="center">
            {missingRewardCount} reward{missingRewardCount === 1 ? '' : 's'} could not be matched yet. Refresh and try
            again in a moment.
          </Typography>
        </Paper>
      )}
      {rewardsByCategory.length === 0 ? (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography color="textSecondary" textAlign="center" py={4}>
            No rewards available. Create rewards first in Manage Rewards.
          </Typography>
        </Paper>
      ) : (
        <>
          {rewardsByCategory.map((category, index) => (
            <PackageCategoryCard
              key={category.categoryName}
              categoryName={category.categoryName}
              rewards={category.rewards}
              onToggleRewardSelect={onToggleRewardSelect}
              onContributionChange={onContributionChange}
              onReorderRewards={onReorderRewards}
              formatCurrency={formatCurrency}
              theme={theme}
              showSelectAll={index === 0}
              allSelected={allSelected}
              someSelected={someSelected}
              onToggleAll={onToggleAll}
            />
          ))}
        </>
      )}
    </>
  )

  const renderStep3 = () => (
    <>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          <strong>Package Details</strong>
        </Typography>
        <Typography>
          <strong>Name:</strong> {name}
        </Typography>
        {description && (
          <Typography sx={{ mt: 1 }}>
            <strong>Header Information:</strong> {description}
          </Typography>
        )}
        {disclaimer && (
          <Typography sx={{ mt: 1 }}>
            <strong>Disclaimer:</strong> {disclaimer}
          </Typography>
        )}
        <Typography sx={{ mt: 1 }}>
          <strong>Selected Rewards:</strong> {selectedRewardsCount}
        </Typography>
      </Paper>

      {selectedRewardsCount === 0 ? (
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography color="textSecondary" textAlign="center" py={2}>
            No rewards selected
          </Typography>
        </Paper>
      ) : (
        <>
          {selectedRewardsByCategory.map((category) => (
            <ReviewCategoryCard
              key={category.categoryName}
              categoryName={category.categoryName}
              rewards={category.rewards}
              theme={theme}
            />
          ))}

          <Paper sx={{ p: 2, mt: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} px={1.5}>
              <Box sx={{ flexGrow: 1, minWidth: 120 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  Totals
                </Typography>
              </Box>
              <Box sx={{ width: 140, textAlign: 'right', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  ${totals.company.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ width: 140, textAlign: 'right', pr: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.95rem' }}>
                  ${totals.employee.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </>
  )

  return (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {activeStep === 0 && renderStep1()}
      {activeStep === 1 && renderStep2()}
      {activeStep === 2 && renderStep3()}

      {errorMessage && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <AnimateButton>
            <Button variant="contained" color="warning" onClick={onCancel}>
              Back to Rewards
            </Button>
          </AnimateButton>

          <Stack direction="row" alignItems="center" spacing={2}>
            {activeStep === 1 && (
              <Typography variant="body2" color="textSecondary">
                {selectedRewardsCount} reward{selectedRewardsCount !== 1 ? 's' : ''} selected
              </Typography>
            )}

            {activeStep > 0 && (
              <Button variant="outlined" onClick={onBack}>
                Back
              </Button>
            )}

            {activeStep < steps.length - 1 ? (
              <AnimateButton>
                <Button variant="contained" onClick={onNext} disabled={activeStep === 0 && !isNameValid}>
                  Next
                </Button>
              </AnimateButton>
            ) : (
              <AnimateButton>
                <Button variant="contained" color="primary" onClick={onCreate} disabled={isSubmitting || !canSubmit}>
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Create Package'}
                </Button>
              </AnimateButton>
            )}
          </Stack>
        </Stack>
      </Paper>
    </>
  )
}

export default SmartImportPackageForm
